import styles from "./styles.module.scss";
import { LogoWithText, ArrowLeft, MenuIcon } from 'assets';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressSidebar, QuestionSingleRadio, QuestionRanking, QuestionTextarea, Spinner, RankingField, QuestionCheckboxWithRating, QuestionMultiCheckbox, QuestionSkillsRating } from 'components';
import { SubmitAssessmentModal } from 'components/modal-variants';
import { useClickOutside, useGetAssessmentInfo, useGetDefaultQuestions, useMobile, useToast, useCheckCandidateProgress } from "hooks";
import { apiClient } from 'api';
import { openFullScreen, exitFullScreen, isFullScreen } from 'helpers';
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CountDown } from "components/countdown";
import { ProductivityToolsInput } from "components/proficiency-tools-form";
import VerticalRankingField from "components/vertical-drag-input";
import { useSubmitAssessmentSections } from "hooks/useUpdateAssessmentResponse";
import { useCompleteCandidateSubmission, SubmissionMode } from "hooks/useCompleteCandidateSubmission";
import { buildSectionedPayloads, saveFormStateToLocalStorage, loadFormStateFromLocalStorage, SectionSlug } from "utils/assessmentSubmissionHelpers";
import {
  OPEN_ENDED_RESPONSE_MIN_WORDS,
  meetsOpenEndedResponseMinimum,
} from 'utils/openEndedResponse';
import { useDispatch, useSelector } from 'react-redux';
import { setAssessmentId, updateMinutesLeft } from 'state/actions/assessment';
import { useResponsesStore } from 'state/zustand/assessmentResponses';
import { RootReducerState } from 'state/reducers';
import {
  employmentTypeOptions,
  experienceLevelOptions,
  yearsOfExperienceOptions,
  workEnvironmentOptions,
} from 'components/edit-modals';

// Master switch for tab-switch proctoring (warning + auto-submit on tab/window
// change). Disabled while we do extensive internal testing — the detection was
// producing false positives and incomplete sessions. Flip to `true` to re-enable
// without touching the detection logic below.
const TAB_SWITCH_DETECTION_ENABLED = true;

// Minimal Screen Wake Lock typings. The DOM lib bundled with our TS version does
// not yet declare these, so we describe just the surface we use. The API is a
// native browser capability (no dependency, no cost) — it asks the OS to keep
// the screen awake so the device doesn't dim/lock mid-assessment.
interface WakeLockSentinelLike {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
}
type NavigatorWithWakeLock = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
};

interface Option {
  label: string;
  value: string;
  score?: number;
  id?: string;
  text?: string;
  color?: string;
  position?: number;
  rank?: number;
  percentage?: number;
}

interface Question {
  id: string;
  type: 'ranking' | 'vertical_ranking' | 'single_choice_with_rating' | 'multiple_choice' | 'scale' | 'statement_rating' | 'rating' | 'multi_select' | 'multiple_select' | 'skill_rating' | 'matching' | 'text' | 'productivity_tools' | 'multiple_rating' | 'yes_no' | 'true_false' | 'open_ended';
  question?: string;
  text?: string;
  options?: Option[];
  scale_max?: number;
  scale_min?: number;
  scale_min_label?: string;
  scale_max_label?: string;
  calculation_info?: { red_flags: string[]; ideal_keywords: string[] };
  category?: string;
  statements?: string[];
  activities?: string[];
  select_count?: number;
  items?: any[];
  correct_order?: any[];
  field?: string; // API field name for submission
  description?: string;
  image?: string | null;
  image_description?: string;
  required?: boolean;
  points_obtainable?: number;
  maximum_selections?: number;
  correct_answer?: string;
  hasOthersOption?: boolean;
  othersField?: string;
  section?: SectionSlug;
  backendId?: number;
  // Conditional (gated) question support. When set, this question is only shown
  // if the gate question identified by `conditionalGate` was answered with the
  // matching yes/no branch (`conditionalShowWhen`).
  conditionalGate?: string;
  conditionalShowWhen?: 'yes' | 'no';
}

interface CategorizedQuestions {
  [key: string]: Question[];
}

interface QuestionSection {
  id: string;
  label: string;
  questions: Question[];
}

export interface FormValues {
  [key: string]: any;
}

const generateSchema = (questions: CategorizedQuestions) => {
  const shape: { [key: string]: any } = {};

  Object.values(questions).flat().forEach((q) => {
    if (q.type === 'multiple_choice' || q.type === 'single_choice_with_rating' || q.type === 'matching') {
      shape[`question_${q.id}`] = yup
        .string()
        .nullable()
        .optional()
        .transform((value, originalValue) => {
          if (Array.isArray(originalValue) && originalValue.length === 0) {
            return null;
          }
          return originalValue;
        });
    } else if (q.type === 'multi_select' || q.type === 'multiple_select') {
      shape[`question_${q.id}`] = yup
        .array()
        .of(yup.string())
        .nullable()
        .optional()
        .test('select-count', `Please select exactly ${q.select_count} options`, (value) => {
          return q.select_count ? (value?.length || 0) === q.select_count : true;
        });
    } else if (q.type === 'scale' || q.type === 'rating') {
      shape[`question_${q.id}`] = yup
        .number()
        .nullable()
        .optional()
        .min(q.scale_min || 1, `Rating must be at least ${q.scale_min || 1}`)
        .max(q.scale_max || 5, `Rating must be at most ${q.scale_max || 5}`)
        .transform((value, originalValue) => (originalValue === '' || (Array.isArray(originalValue) && originalValue.length === 0) ? null : value));
    } else if (q.type === 'open_ended') {
      // Open ended questions for domain knowledge.
      shape[`question_${q.id}`] = yup
        .string()
        .nullable()
        .optional()
        .test(
          'min-words',
          `Response must be at least ${OPEN_ENDED_RESPONSE_MIN_WORDS} words, or left empty`,
          meetsOpenEndedResponseMinimum,
        );
    } else if (q.type === 'statement_rating') {
      shape[`question_${q.id}`] = yup
        .string()
        .nullable()
        .optional();
      // Also add the rating field
      shape[`question_${q.id}Rating`] = yup
        .number()
        .nullable()
        .optional()
        .min(1)
        .max(q.scale_max || 5);
    } else if (q.type === 'multiple_rating') {
      // Multiple rating stores an object with keys a, b, c, etc.
      shape[`question_${q.id}`] = yup
        .object()
        .nullable()
        .optional()
        .test('not-all-max-ratings', 'Selecting the highest rating for every option is not a logical selection. Please vary your ratings.', (value) => {
          if (!value || typeof value !== 'object') return true;

          const ratings = Object.values(value).filter((rating) => rating !== null && rating !== undefined && rating !== '');
          if (ratings.length <= 1) return true;

          return !ratings.every((rating) => Number(rating) === (q.scale_max || 7));
        });
    } else if (q.type === 'vertical_ranking') {
      shape[`question_${q.id}`] = yup
        .array()
        .of(
          yup.object().shape({
            id: yup.string().required(),
            text: yup.string().required(),
            rank: yup.number().required(),
          }).nullable()
        )
        .nullable()
        .optional();
    } else if (q.type === 'ranking') {
      shape[`question_${q.id}`] = yup
        .array()
        .of(
          yup.object().shape({
            id: yup.string().required(),
            text: yup.string().required(),
            color: yup.string().required(),
            position: yup.number().required(),
          }).nullable()
        )
        .nullable()
        .optional();
    } else if (q.type === 'text') {
      shape[`question_${q.id}`] = yup
        .string()
        .nullable()
        .optional()
        .test(
          'min-words',
          `Response must be at least ${OPEN_ENDED_RESPONSE_MIN_WORDS} words, or left empty`,
          meetsOpenEndedResponseMinimum,
        );
    } else if (q.type === 'yes_no' || q.type === 'true_false') {
      shape[`question_${q.id}`] = yup
        .string()
        .nullable()
        .optional();
    }
  });

  if (Object.values(questions).flat().some(q => q.type === 'productivity_tools')) {
    shape.tools = yup
      .array()
      .of(
        yup.object().shape({
          tool: yup.string().nullable().optional(),
          proficiency: yup
            .object()
            .shape({
              value: yup.string().nullable().optional(),
              label: yup.string().nullable().optional(),
            })
            .nullable()
            .optional()
        })
      )
      .nullable()
      .optional();
  }

  return yup.object().shape(shape);
};

// Gate answers stored by the yes/no radio are the strings 'true'/'false'; some
// older payloads use 'yes'/'no'. Treat both as the affirmative branch.
const GATE_YES_VALUES = new Set(['true', 'yes']);

// A conditional (gated) question — e.g. the Leadership & Management follow-ups —
// is only shown when its gate question's yes/no answer matches the branch it
// belongs to. Non-conditional questions are always visible. While the gate is
// still unanswered, neither branch shows. Pure so it can drive both render-time
// filtering and the submission payload (which must never include the branch the
// candidate didn't take).
const isConditionalQuestionVisible = (
  question: Question,
  formValues: Record<string, any>,
): boolean => {
  if (!question.conditionalGate || !question.conditionalShowWhen) return true;
  const gateValue = formValues[`question_${question.conditionalGate}`];
  if (gateValue === undefined || gateValue === null || gateValue === '') return false;
  const isYes = GATE_YES_VALUES.has(String(gateValue).toLowerCase());
  return question.conditionalShowWhen === 'yes' ? isYes : !isYes;
};

// A field value counts as "present" when it holds a real answer — not empty
// string, empty array, or empty object.
const valueIsPresent = (val: any): boolean => {
  if (val === null || val === undefined || val === '') return false;
  if (Array.isArray(val) && val.length === 0) return false;
  if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) return false;
  return true;
};

// Whether a question still needs an answer. `resolveValue` returns the effective
// value for a field name (live form value falling back to the persisted backup),
// so the same per-type logic drives the submit-time count, the per-section
// breakdown, and the live "Unanswered" badges shown in review mode.
const isQuestionUnanswered = (
  question: Question,
  resolveValue: (fieldName: string) => any,
): boolean => {
  const fieldName = question.type === 'productivity_tools' ? 'tools' : `question_${question.id}`;
  const value = resolveValue(fieldName);

  if (question.type === 'single_choice_with_rating' || question.type === 'statement_rating') {
    const ratingValue = resolveValue(`${fieldName}Rating`);
    return !valueIsPresent(value) || !valueIsPresent(ratingValue);
  }
  if (question.type === 'multi_select' || question.type === 'multiple_select') {
    return !Array.isArray(value) || value.length === 0;
  }
  if (question.type === 'vertical_ranking') {
    return !Array.isArray(value) || value.length === 0 || value.every((v: any) => v === null || v === undefined);
  }
  if (question.type === 'ranking') {
    return !Array.isArray(value) || value.every((v: any) => v === null || v === undefined);
  }
  if (question.type === 'productivity_tools') {
    return !Array.isArray(value) || value.length === 0 || !value[0]?.tool;
  }
  if (question.type === 'multiple_rating') {
    return !value || typeof value !== 'object' || Object.keys(value).length === 0;
  }
  return !valueIsPresent(value);
};

const AssessmentUI = () => {
  const questionsEndRef = useRef<HTMLDivElement>(null);
  // The scrollable section that holds the current page of questions.
  const questionsContainerRef = useRef<HTMLDivElement>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openToast } = useToast();
  const tabSwitchStorageKey = id ? `assessment_tab_switch_count_${id}` : 'assessment_tab_switch_count';
  const { fetchAssessmentInfo, assessmentInfo, isLoading: isLoadingGetAssessmentInfo, error: assessmentInfoError } = useGetAssessmentInfo(id ?? '');
  const { defaultQuestions, isLoading: isLoadingQuestions, error: questionsError } = useGetDefaultQuestions(id ?? '');

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
  const [activeSection, setActiveSection] = useState<string>('multiple_choice');
  const [currentPages, setCurrentPages] = useState<{ [key: string]: number }>({});
  const [categorizedQuestions, setCategorizedQuestions] = useState<CategorizedQuestions>({});
  const [questionSections, setQuestionSections] = useState<QuestionSection[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState<boolean>(false);
  // Turns on when the candidate picks "Review Questions" from the submit modal,
  // so unanswered questions get an inline "Unanswered" badge as they navigate.
  const [reviewMode, setReviewMode] = useState<boolean>(false);
  const [endTime, setEndTime] = useState<Date | null>(() => {
    try {
      const saved = localStorage.getItem('assessment_response_data');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.end_time) return new Date(parsed.end_time);
        if (parsed.duration_minutes_left !== undefined) {
          return new Date(Date.now() + parsed.duration_minutes_left * 60000);
        }
      }
    } catch {}
    return null;
  });
  const [multipleRatingStates, setMultipleRatingStates] = useState<{ [key: string]: { [key: string]: number } }>({});
  const [showTabSwitchWarning, setShowTabSwitchWarning] = useState(false);
  // Which proctoring violation triggered the current warning, so the modal can
  // show the right copy. Fullscreen exit and tab switch share one strike count
  // and the same warn-then-auto-submit flow, but read differently to the user.
  const [warningReason, setWarningReason] = useState<'tab-switch' | 'fullscreen-exit'>('tab-switch');
  const tabSwitchCountRef = useRef(
    (() => {
      const stored = sessionStorage.getItem(tabSwitchStorageKey);
      return stored ? parseInt(stored, 10) : 0;
    })()
  );

  // Redux hooks
  const dispatch = useDispatch();
  const assessmentState = useSelector((state: RootReducerState) => state?.assessment);
  const { mutateAsync: submitSections, isPending: isUpdating } = useSubmitAssessmentSections();
  const { mutateAsync: completeSubmission } = useCompleteCandidateSubmission();
  const { mutate: checkCandidateProgress } = useCheckCandidateProgress();

  const currentCandidateEmail = (localStorage.getItem('candidate_email') || '').trim().toLowerCase();
  const getSubmittedStateKey = useCallback((assessmentId: string) => `assessment_submitted_${assessmentId}`, []);
  const getSubmittedEmailKey = useCallback((assessmentId: string) => `assessment_submitted_email_${assessmentId}`, []);
  const bootstrapAssessmentResponseRef = useRef(false);

  // Fire-and-forget per-section save invoked on every page/section navigation.
  // Keeps the candidate's progress on the server without blocking the UI; the
  // final Submit re-posts everything as a safety net if any of these silently
  // failed.
  const persistSectionToServer = useCallback((sectionSlug: string | undefined, formValues: Record<string, any>) => {
    const responseId = localStorage.getItem('assessment_response_id');
    if (!responseId || !sectionSlug) return;
    if (!formValues || Object.keys(formValues).length === 0) return;

    const allQs = Object.values(categorizedQuestions)
      .flat()
      .filter((q) => isConditionalQuestionVisible(q, formValues));
    if (allQs.length === 0) return;

    const minutesLeft = endTime
      ? Math.max(0, Math.floor((endTime.getTime() - Date.now()) / 60000))
      : (assessmentState?.minutesLeft ?? 0);
    const payloads = buildSectionedPayloads(formValues, allQs, { minutesLeft });
    const body = (payloads as Record<string, any>)[sectionSlug];
    if (!body) return;

    apiClient
      .post(`/candidate/${sectionSlug}/${responseId}/`, body)
      .catch(() => {
        // silent — final submit retries everything
      });
  }, [categorizedQuestions, endTime, assessmentState?.minutesLeft]);

  const finalizeAssessmentSubmission = useCallback(async (assessmentResponseId: string, sectionedPayloads: Partial<Record<SectionSlug, Record<string, any>>>, submissionMode: SubmissionMode) => {
    await submitSections({
      assessmentId: assessmentResponseId,
      sectionedPayloads,
    });

    await completeSubmission({
      responseId: Number(assessmentResponseId),
      submissionMode,
    });

    if (id) {
      localStorage.setItem(getSubmittedStateKey(id), 'true');
      if (currentCandidateEmail) {
        localStorage.setItem(getSubmittedEmailKey(id), currentCandidateEmail);
      }
    }

    localStorage.removeItem('assessment_form_state');
    localStorage.removeItem('assessment-responses');
    sessionStorage.removeItem(tabSwitchStorageKey);

    navigate(`/assessment-feedback/${assessmentResponseId}`);
  }, [completeSubmission, currentCandidateEmail, getSubmittedEmailKey, getSubmittedStateKey, id, navigate, submitSections, tabSwitchStorageKey]);

  const persistAssessmentBootstrapData = useCallback((payload: Record<string, any>, responseData: any) => {
    if (!responseData) return;

    if (responseData.id) {
      localStorage.setItem('assessment_response_id', String(responseData.id));
    }

    localStorage.setItem('candidate_email', payload.email);

    localStorage.setItem('assessment_response_data', JSON.stringify({
      id: responseData.id,
      end_time: responseData.end_time,
      duration_minutes: responseData.duration_minutes,
      duration_minutes_left: responseData.duration_minutes_left,
      status: responseData.status,
    }));

    if (responseData.end_time) {
      setEndTime(new Date(responseData.end_time));
    } else if (responseData.duration_minutes_left !== undefined) {
      setEndTime(new Date(Date.now() + Number(responseData.duration_minutes_left) * 60000));
    }
  }, [setEndTime]);

  const createBootstrapAssessmentPayload = useCallback((assessmentIdValue: string) => {
    const bootstrapSuffix = `${assessmentIdValue}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

    const employmentTypeValue = employmentTypeOptions[0]?.value || 'fulltime';
    const experienceLevelValue = experienceLevelOptions[2]?.value || 'mid';
    const yearsOfExperienceValue = yearsOfExperienceOptions[1]?.value || '1_3';
    const workEnvironmentValue = workEnvironmentOptions[0]?.value || 'startup';

    return {
      assessment: assessmentIdValue,
      full_name: 'Assessment Candidate',
      email: `candidate-${bootstrapSuffix}@example.com`,
      portfolio: 'https://example.com',
      resume: '',
      phone_number: '0000000000',
      minutes_left: assessmentState?.minutesLeft ?? 60,
      role_source: 'precision',
      basic_expectation: {
        location: 'remote',
        exp_level: experienceLevelValue,
        exp_years: yearsOfExperienceValue,
        emp_type: employmentTypeValue,
        salary_expectation: '0',
        exp_environment: workEnvironmentValue,
        role_source: 'precision',
      },
    };
  }, [assessmentState?.minutesLeft]);

  // Synchronous gate: compute before any other hooks run derived work.
  // A candidate who already submitted (or whose timer ran out) must not
  // see the questions UI or let its mutating effects run.
  const isAlreadySubmitted = id
    ? localStorage.getItem(getSubmittedStateKey(id)) === 'true' && localStorage.getItem(getSubmittedEmailKey(id))?.trim().toLowerCase() === currentCandidateEmail
    : false;
  const hasTimedOut = endTime ? endTime.getTime() <= Date.now() : false;
  const shouldBlockAssessment = isAlreadySubmitted || hasTimedOut;

  useEffect(() => {
    if (!id || bootstrapAssessmentResponseRef.current) return;
    if (localStorage.getItem('assessment_response_id')) return;

    bootstrapAssessmentResponseRef.current = true;

    const bootstrap = async () => {
      try {
        const payload = createBootstrapAssessmentPayload(id);
        const response = await apiClient.post('/candidate/basic-expectation/', payload);
        const responseData = response.data?.data || response.data;
        persistAssessmentBootstrapData(payload, responseData);
      } catch (error) {
        bootstrapAssessmentResponseRef.current = false;
        console.error('Failed to bootstrap assessment response id:', error);
      }
    };

    void bootstrap();
  }, [createBootstrapAssessmentPayload, id, persistAssessmentBootstrapData]);

  useEffect(() => {
    if (id && shouldBlockAssessment) {
      if (hasTimedOut && !isAlreadySubmitted) {
        localStorage.setItem(getSubmittedStateKey(id), 'true');
        if (currentCandidateEmail) {
          localStorage.setItem(getSubmittedEmailKey(id), currentCandidateEmail);
        }
      }
      const assessment_response_id = localStorage.getItem('assessment_response_id') || id;
      navigate(`/assessment-feedback/${assessment_response_id}`, { replace: true });
    }
  }, [currentCandidateEmail, getSubmittedEmailKey, getSubmittedStateKey, id, navigate, shouldBlockAssessment, hasTimedOut, isAlreadySubmitted]);

  const questionsPerPage = 5;
  const countdownDate = endTime;

  // Guard so the timeout handler never fires twice (e.g. if CountDown re-runs
  // its effect on re-render). Without this, a double-fire submits twice and
  // can drive a render loop against a new handleTimeout reference.
  const timeoutHandledRef = useRef(false);

  const generateQuestionId = (prefix: string, index: number) => {
    // Use a stable ID that doesn't change across page reloads
    // This is critical for persistence to work correctly
    return `${prefix}_${index}`;
  };

  useEffect(() => {
    if (defaultQuestions?.length > 0 && defaultQuestions[0]?.questions) {
      const data = defaultQuestions[0].questions;
      const newCategorizedQuestions: CategorizedQuestions = {};
      const newQuestionSections: QuestionSection[] = [];

      const mapOptions = (options: any[] = [], questionId: string) => options.map((opt: any, optIdx: number) => ({
        id: opt.id || `${questionId}_opt_${optIdx}`,
        label: opt.label || opt.text || opt,
        value: opt.value || opt.label || opt.text || opt.score?.toString(),
        score: opt.score,
        text: opt.label || opt.text || opt,
        color: opt.color || '#ffffff',
        position: opt.position || optIdx + 1,
        percentage: opt.percentage,
      }));

      const buildQuestion = (source: any, questionId: string): Question => ({
        id: questionId,
        type: source.type === 'skill_rating' ? 'productivity_tools' : source.type,
        question: source.question || source.text || source.question_content,
        text: source.text || source.question || source.question_content,
        options: mapOptions(source.options || [], questionId),
        correct_order: source.correct_order || undefined,
        items: source.items || undefined,
        scale_max: source.rating_scale?.max || source.scale_max,
        scale_min: source.rating_scale?.min || source.scale_min,
        scale_min_label: source.scale_min_label,
        scale_max_label: source.scale_max_label,
        category: source.category,
        statements: source.statements,
        activities: source.activities,
        select_count: source.select_count,
        field: source.field,
        hasOthersOption: source.hasOthersOption,
        othersField: source.othersField,
        description: source.description,
        image: source.image,
        image_description: source.image_description,
        required: source.required,
        points_obtainable: source.points_obtainable,
        maximum_selections: source.maximum_selections,
        backendId: source.question_id || source.id,
        section: source.section,
        conditionalGate: source.conditionalGate,
        conditionalShowWhen: source.conditionalShowWhen,
      });

      const addSection = (id: string, label: string, questions: Question[]) => {
        if (!questions.length) return;
        newCategorizedQuestions[id] = questions;
        newQuestionSections.push({ id, label, questions });
      };

      const workStyleQuestions: Question[] = [];
      (data.work_style?.candidate?.questions || []).forEach((competency: any, compIdx: number) => {
        // Iterate over whatever experience-level keys the backend nests questions
        // under (entry / associate / mid / senior) rather than a hardcoded subset,
        // so associate-level assessments don't silently drop their questions.
        Object.keys(competency.questions || {}).forEach((level) => {
          (competency.questions?.[level] || []).forEach((q: any, qIdx: number) => {
            const questionId = generateQuestionId(`work_style_${competency.competency}_${level}`, qIdx);
            workStyleQuestions.push(buildQuestion({
              ...q,
              field: q.field || `${competency.competency.toLowerCase().replace(/\s+/g, '_')}_${level}_q${qIdx + 1}_ranking`,
              section: 'work-style',
            }, questionId));
          });
        });
      });
      addSection('work-style', 'Execution Approach', workStyleQuestions);

      const personalityQuestions: Question[] = [];
      (Array.isArray(data.personality) ? data.personality : data.personality ? [data.personality] : []).forEach((pq: any, pqIdx: number) => {
        const questionId = generateQuestionId('personality', pqIdx);
        personalityQuestions.push(buildQuestion({
          ...pq,
          field: pq.field || `personality_q${pq.number || pqIdx + 1}`,
          options: pq.options?.map((opt: any) => typeof opt === 'string' ? { label: opt, value: opt, text: opt } : opt) || [],
          section: 'personality',
        }, questionId));
      });
      addSection('personality', 'Personality', personalityQuestions);

      const workMotivatorsQuestions: Question[] = [];
      (data.work_motivators?.candidate?.questions || []).forEach((motivator: any) => {
        (motivator.questions || []).forEach((q: any, qIdx: number) => {
          const questionId = generateQuestionId(`work_motivators_${motivator.motivator}`, qIdx);
          workMotivatorsQuestions.push(buildQuestion({
            ...q,
            field: q.field || `${motivator.motivator.toLowerCase().replace(/\s+/g, '_')}_q${qIdx + 1}_${q.type === 'scale' ? 'rating' : 'choice'}`,
            section: 'work-motivators',
          }, questionId));
        });
      });
      addSection('work-motivators', 'Work Drivers', workMotivatorsQuestions);

      const softSkillQuestions: Question[] = [];
      (data.soft_competencies || []).forEach((comp: any, compIdx: number) => {
        // Capture the gate question's id so the yes/no follow-up branches can be
        // tied back to it and shown conditionally on the candidate's answer.
        let gateQuestionId: string | undefined;
        if (comp.conditional_start || comp.gate_question) {
          const gateQ = comp.conditional_start || comp.gate_question;
          gateQuestionId = generateQuestionId(`soft_competencies_${comp.competency}_gate`, compIdx);
          softSkillQuestions.push(buildQuestion({
            ...gateQ,
            type: gateQ.type === 'yes_no' ? 'yes_no' : 'true_false',
            text: gateQ.text,
            question: gateQ.text,
            field: comp.competency.toLowerCase().replace(/\s+/g, '_').replace(/\//g, '_') + '_experience',
            options: gateQ.options?.map((opt: string) => ({ label: opt, value: opt.toLowerCase(), text: opt })) || [
              { label: 'Yes', value: 'true', text: 'Yes' },
              { label: 'No', value: 'false', text: 'No' },
            ],
            section: 'soft-skill',
          }, gateQuestionId));
        }

        const appendQuestions = (
          questions: any[] = [],
          suffix = '',
          conditional?: { gateId: string; showWhen: 'yes' | 'no' },
        ) => {
          questions.forEach((q: any, qIdx: number) => {
            const questionId = generateQuestionId(`soft_competencies_${comp.competency}${suffix}`, qIdx);
            softSkillQuestions.push(buildQuestion({
              ...q,
              field: q.field || `${comp.competency.toLowerCase().replace(/\s+/g, '_').replace(/\//g, '_')}_q${qIdx + 1}`,
              hasOthersOption: q.hasOthersOption || q.options?.some((opt: any) => (opt.text || opt.label || '').toLowerCase().includes('other')),
              section: 'soft-skill',
              conditionalGate: conditional?.gateId,
              conditionalShowWhen: conditional?.showWhen,
            }, questionId));
          });
        };

        appendQuestions(comp.questions || []);
        appendQuestions(
          comp.questions_if_yes || [],
          '_yes',
          gateQuestionId ? { gateId: gateQuestionId, showWhen: 'yes' } : undefined,
        );
        appendQuestions(
          comp.questions_if_no || [],
          '_no',
          gateQuestionId ? { gateId: gateQuestionId, showWhen: 'no' } : undefined,
        );
      });
      addSection('soft-skill', 'Interpersonal Effectiveness', softSkillQuestions);

      const workQualityQuestions: Question[] = [];
      (data.workplace_qualities || []).forEach((quality: any, qualityIdx: number) => {
        (quality.questions || []).forEach((q: any, qIdx: number) => {
          const questionId = generateQuestionId(`workplace_qualities_${quality.quality}`, qIdx);
          workQualityQuestions.push(buildQuestion({
            ...q,
            field: q.field || `${quality.quality.toLowerCase().replace(/\s+/g, '_')}_q${qIdx + 1}_${q.type === 'scale' ? 'rating' : 'choice'}`,
            section: 'work-qualities',
          }, questionId));
        });
      });
      addSection('work-qualities', 'Work Environment Preferences', workQualityQuestions);

      const domainKnowledgeQuestions: Question[] = [];
      const domainKnowledgeFieldMapping = data.domain_knowledge?.field_mapping?.field_mapping || {};
      (data.domain_knowledge?.question_groups || []).forEach((group: any, groupIdx: number) => {
        (group.questions || []).forEach((q: any, qIdx: number) => {
          const questionId = generateQuestionId(`domain_g${groupIdx + 1}`, qIdx);
          const mappedQuestionId = domainKnowledgeFieldMapping[q.field || `domain_g${groupIdx + 1}_q${qIdx + 1}`]?.question_id;
          domainKnowledgeQuestions.push(buildQuestion({
            ...q,
            type: q.question_type === 'open_ended' ? 'text' : q.question_type,
            question: q.question_content,
            text: q.question_content,
            field: q.field || q.id || `domain_g${groupIdx + 1}_q${qIdx + 1}`,
            options: q.options?.map((opt: any, optIdx: number) => ({
              id: `${questionId}_opt_${optIdx}`,
              label: opt.content,
              value: opt.content,
              text: opt.content,
              score: opt.correct ? 1 : 0,
              correct: opt.correct,
              color: '#ffffff',
              position: optIdx + 1,
            })) || [],
            section: 'domain-knowledge',
            question_id: mappedQuestionId || q.question_id || (typeof q.id === 'number' ? q.id : undefined),
          }, questionId));
        });
      });
      addSection('domain-knowledge', 'Professional Expertise', domainKnowledgeQuestions);

      const newCurrentPages: { [key: string]: number } = {};
      newQuestionSections.forEach((section) => {
        newCurrentPages[section.id] = 1;
      });

      setCurrentPages(newCurrentPages);
      setCategorizedQuestions(newCategorizedQuestions);
      setQuestionSections(newQuestionSections);
      setActiveSection(newQuestionSections[0]?.id || 'work-style');
    }
  }, [defaultQuestions, questionsError]);

  useEffect(() => {
    if (assessmentInfoError || questionsError) {
      setHasError(true);
      openToast({ show: true, heading: 'Error', text: 'Failed to load assessment data. Please try again.', type: 'error', timeOut: 5000 });
    }
  }, [assessmentInfoError, openToast, questionsError]);

  const { register, watch, setValue, getValues, reset, formState: { errors }, control, trigger } = useForm<FormValues>({
    resolver: yupResolver(generateSchema(categorizedQuestions)),
    defaultValues: {},
    mode: 'onChange',
  });

  // Zustand responses store
  const persistedResponses = useResponsesStore((s) => s.responses);
  const setResp = useResponsesStore((s) => s.setResponse);
  const setResps = useResponsesStore((s) => s.setResponses);

  // Reactively track the gate (yes/no) answers that drive conditional questions
  // so pagination, progress counts and rendering all update the instant a gate
  // is answered. We subscribe to only the gate fields to avoid re-rendering the
  // whole form on every keystroke elsewhere.
  const conditionalGateFieldNames = useMemo(() => {
    const names = new Set<string>();
    Object.values(categorizedQuestions).flat().forEach((q) => {
      if (q.conditionalGate) names.add(`question_${q.conditionalGate}`);
    });
    return Array.from(names);
  }, [categorizedQuestions]);

  // Watch a phantom name when there are no gates so `watch` is never handed an
  // empty array (which would subscribe to the entire form).
  const watchedGateValues = watch(
    conditionalGateFieldNames.length ? conditionalGateFieldNames : ['__no_conditional_gate__'],
  );
  const gateValues = useMemo(() => {
    const map: Record<string, any> = {};
    conditionalGateFieldNames.forEach((name, i) => {
      map[name] = (watchedGateValues as any[])?.[i];
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionalGateFieldNames, watchedGateValues]);

  // Merge the live form values with the persisted backups (localStorage + Zustand)
  // so every submit path captures the FULL assessment. react-hook-form can drop
  // values for sections that have been unmounted during navigation, so the live
  // form alone is not a reliable source — earlier sections survive only in the
  // backups. Most-recent source wins (localStorage < Zustand < live form).
  const collectMergedFormValues = useCallback((): Record<string, any> => {
    const formValues = getValues();
    const zustandResponses = useResponsesStore.getState().responses;
    const localStorageState = loadFormStateFromLocalStorage();

    const mergedValues: Record<string, any> = {};

    if (localStorageState) {
      Object.entries(localStorageState).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') mergedValues[key] = val;
      });
    }

    if (zustandResponses) {
      Object.entries(zustandResponses).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') mergedValues[key] = val;
      });
    }

    Object.entries(formValues).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '' &&
          !(Array.isArray(val) && val.length === 0) &&
          !(typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0)) {
        mergedValues[key] = val;
      }
    });

    return mergedValues;
  }, [getValues]);

  const handleTimeout = useCallback(async () => {
    if (timeoutHandledRef.current) return;
    timeoutHandledRef.current = true;

    openToast({ show: true, heading: 'Time Up', text: 'The assessment time has expired.', type: 'error', timeOut: 5000 });

    if (!assessmentState?.assessmentId) return;

    const formValues = collectMergedFormValues();
    const allQuestions = Object.values(categorizedQuestions)
      .flat()
      .filter((q) => isConditionalQuestionVisible(q, formValues));
    const sectionedPayloads = buildSectionedPayloads(formValues, allQuestions, { minutesLeft: 0 });
    const assessment_response_id = localStorage.getItem('assessment_response_id');

    try {
      if (assessment_response_id) {
        await finalizeAssessmentSubmission(assessment_response_id, sectionedPayloads, 'auto_submit_timeout');
      }
    } catch (error) {
      console.error('Failed to submit assessment on timeout:', error);
    }
  }, [assessmentState, categorizedQuestions, collectMergedFormValues, finalizeAssessmentSubmission, openToast]);

  // Auto-submit assessment (used by tab-switch enforcement)
  const autoSubmitAssessment = useCallback(async () => {
    if (!assessmentState?.assessmentId) return;

    const formValues = collectMergedFormValues();
    const allQuestions = Object.values(categorizedQuestions)
      .flat()
      .filter((q) => isConditionalQuestionVisible(q, formValues));
    const minutesLeft = endTime
      ? Math.max(0, Math.floor((endTime.getTime() - Date.now()) / 60000))
      : (assessmentState?.minutesLeft || 0);
    const sectionedPayloads = buildSectionedPayloads(formValues, allQuestions, { minutesLeft });
    const assessment_response_id = localStorage.getItem('assessment_response_id');

    try {
      if (assessment_response_id) {
        await finalizeAssessmentSubmission(assessment_response_id, sectionedPayloads, 'auto_submit_tab_exit');
      }
    } catch (error) {
      console.error('Failed to auto-submit assessment:', error);
    }
  }, [assessmentState, categorizedQuestions, collectMergedFormValues, endTime, finalizeAssessmentSubmission]);

  // Tab-switch detection: warn on first violation, auto-submit on the second.
  const pendingAutoSubmitRef = useRef(false);
  // Set when WE exit fullscreen on purpose (cleanup / after submit) so the
  // fullscreenchange handler doesn't count it as a violation.
  const programmaticFullscreenExitRef = useRef(false);

  // On touch devices, `visibilitychange`/`blur` fire constantly for reasons that
  // are NOT cheating — screen lock, incoming calls, notification shade, the
  // on-screen keyboard, brief app switches. Auto-submitting on those was wiping
  // out genuine, in-progress sessions (candidates reported "my phone locked and
  // it was already submitted"). On touch we warn but never force a submit.
  const isTouchDevice = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return (
      'ontouchstart' in window ||
      (navigator.maxTouchPoints ?? 0) > 0 ||
      Boolean(window.matchMedia?.('(pointer: coarse)').matches)
    );
  }, []);
  const hasOpenInteractivePopup = useCallback(() => {
    if (isSubmitModalOpen) return true;

    return Boolean(
      document.querySelector(
        [
          '[data-assessment-popup-open="true"]',
          '.formSelect__menu-portal',
          '.formSelect__menu',
          '[role="listbox"]',
        ].join(', ')
      )
    );
  }, [isSubmitModalOpen]);

  useEffect(() => {
    const stored = sessionStorage.getItem(tabSwitchStorageKey);
    tabSwitchCountRef.current = stored ? parseInt(stored, 10) : 0;
    pendingAutoSubmitRef.current = false;
    setShowTabSwitchWarning(false);
  }, [tabSwitchStorageKey]);

  useEffect(() => {
    let blurTimeoutId: ReturnType<typeof setTimeout> | null = null;

    // Coalesce near-simultaneous proctoring signals into a single strike. One
    // user action can fire more than one event — e.g. opening a new tab in
    // Chrome both exits fullscreen (fullscreenchange) AND hides the page
    // (visibilitychange). Without this, that single action would count twice and
    // jump straight to auto-submit.
    let lastViolationAt = 0;
    const COALESCE_MS = 800;
    const isDuplicateViolation = () => {
      const now = Date.now();
      if (now - lastViolationAt < COALESCE_MS) return true;
      lastViolationAt = now;
      return false;
    };

    const incrementTabSwitch = () => {
      tabSwitchCountRef.current += 1;
      sessionStorage.setItem(tabSwitchStorageKey, String(tabSwitchCountRef.current));
    };

    const applyTabSwitchConsequence = () => {
      if (tabSwitchCountRef.current === 1) {
        setShowTabSwitchWarning(true);
      } else if (tabSwitchCountRef.current >= 2 && !isTouchDevice) {
        // Never queue an auto-submit on touch devices (see isTouchDevice note).
        pendingAutoSubmitRef.current = true;
      }
    };

    const handleVisibilityChange = () => {
      if (hasOpenInteractivePopup()) {
        return;
      }

      if (document.hidden) {
        // If blur already scheduled an increment, cancel it to avoid double-counting
        if (blurTimeoutId !== null) {
          clearTimeout(blurTimeoutId);
          blurTimeoutId = null;
        }
        if (isDuplicateViolation()) return;
        setWarningReason('tab-switch');
        incrementTabSwitch();
        applyTabSwitchConsequence();
      } else if (pendingAutoSubmitRef.current) {
        // User returned to the tab — now submit
        pendingAutoSubmitRef.current = false;
        setShowTabSwitchWarning(false);
        autoSubmitAssessment();
      }
    };

    const handleWindowBlur = () => {
      if (hasOpenInteractivePopup()) {
        return;
      }

      // Catches Alt-Tab, clicking outside browser, DevTools, etc.
      // Delay briefly so that if visibilitychange fires right after, it can cancel this
      // to avoid double-counting a single tab switch
      if (!document.hidden) {
        blurTimeoutId = setTimeout(() => {
          blurTimeoutId = null;
          if (isDuplicateViolation()) return;
          setWarningReason('tab-switch');
          incrementTabSwitch();
          applyTabSwitchConsequence();
          // blur doesn't defer auto-submit since the user is already away
          if (tabSwitchCountRef.current >= 2) {
            setShowTabSwitchWarning(false);
            autoSubmitAssessment();
          }
        }, 100);
      }
    };

    // Leaving fullscreen is a proctoring violation that shares the same strike
    // count and warn-then-auto-submit flow as a tab switch — only the warning
    // copy differs. The candidate is still on the page, so (like blur) we submit
    // immediately on the second strike rather than deferring. Desktop only: on
    // touch, fullscreen exits constantly for benign reasons (notifications,
    // rotation, system UI) — the same false-positive class we never force-submit
    // on. This handler is only registered when `!isTouchDevice`.
    const handleFullscreenChange = () => {
      // Only react to EXITING fullscreen, not entering it.
      if (isFullScreen()) return;
      // Ignore exits we triggered ourselves (cleanup / after submit).
      if (programmaticFullscreenExitRef.current) {
        programmaticFullscreenExitRef.current = false;
        return;
      }
      if (hasOpenInteractivePopup()) return;
      if (isDuplicateViolation()) return;

      setWarningReason('fullscreen-exit');
      incrementTabSwitch();
      if (tabSwitchCountRef.current === 1) {
        setShowTabSwitchWarning(true);
      } else if (tabSwitchCountRef.current >= 2) {
        setShowTabSwitchWarning(false);
        autoSubmitAssessment();
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Save form state before unload
      const currentFormValues = getValues();
      if (currentFormValues && Object.keys(currentFormValues).length > 0) {
        const allQuestions = Object.values(categorizedQuestions).flat();
        saveFormStateToLocalStorage(currentFormValues, allQuestions.map(q => q.id));
        setResps(currentFormValues);
      }
      // Show browser confirmation dialog to prevent closing/refreshing
      e.preventDefault();
    };

    // `beforeunload` only saves in-progress form state and guards against
    // accidental refresh/close — it is not part of tab-switch proctoring, so it
    // stays registered regardless of the master switch.
    window.addEventListener('beforeunload', handleBeforeUnload);

    if (TAB_SWITCH_DETECTION_ENABLED) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      // `blur` is far too noisy on touch devices (keyboard, app switch, etc.), so
      // only enforce it where it reliably signals a tab switch.
      if (!isTouchDevice) {
        window.addEventListener('blur', handleWindowBlur);
        // Fullscreen enforcement is desktop-only for the same reason (see note
        // on handleFullscreenChange). `webkitfullscreenchange` covers Safari.
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
      }

      // If already over the limit on mount (e.g. user refreshed after warnings),
      // auto-submit immediately — but never on touch, where a stale count from a
      // screen-lock/refresh would submit before the candidate even starts.
      if (tabSwitchCountRef.current >= 2 && !isTouchDevice) {
        autoSubmitAssessment();
      }
    }

    return () => {
      if (blurTimeoutId !== null) clearTimeout(blurTimeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // NOTE: do NOT exit fullscreen here. This effect re-runs whenever its deps
      // change (e.g. when categorizedQuestions loads just after mount), and
      // exiting on every re-run would kick the candidate straight back out of
      // the fullscreen we just entered. Exiting on real unmount is handled by a
      // dedicated [] effect below.
    };
  }, [autoSubmitAssessment, categorizedQuestions, getValues, hasOpenInteractivePopup, isTouchDevice, setResps, tabSwitchStorageKey]);

  // Exit fullscreen only when the candidate truly leaves the assessment, so they
  // aren't left stuck in fullscreen afterwards. Empty deps → runs once on mount,
  // cleans up once on unmount (unlike the proctoring effect above, which re-runs).
  useEffect(() => {
    return () => {
      programmaticFullscreenExitRef.current = true;
      exitFullScreen();
    };
  }, []);

  // Fallback fullscreen entry. The primary request fires on the confirm click in
  // the profile form (a valid gesture) and persists across the navigation here.
  // requestFullscreen can't run without a gesture, so for the cases where that
  // didn't apply — direct navigation, a refresh mid-assessment, or a request the
  // browser rejected — re-enter on the candidate's first interaction here, which
  // IS a gesture. No-ops once we're already fullscreen so it won't fight a user
  // who is legitimately in fullscreen. Desktop only.
  useEffect(() => {
    if (!TAB_SWITCH_DETECTION_ENABLED || isTouchDevice) return;

    const enterIfNeeded = () => {
      if (!isFullScreen()) openFullScreen();
    };

    const onInteraction = () => enterIfNeeded();
    window.addEventListener('pointerdown', onInteraction);
    window.addEventListener('keydown', onInteraction);

    return () => {
      window.removeEventListener('pointerdown', onInteraction);
      window.removeEventListener('keydown', onInteraction);
    };
  }, [isTouchDevice]);

  // Screenshot & screen recording prevention
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        return;
      }
      // Cmd+Shift+3/4/5 (Mac screenshots/recording)
      if (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) {
        e.preventDefault();
        return;
      }
      // Ctrl+Shift+S (Windows screenshot tools)
      if (e.ctrlKey && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        return;
      }
      // Windows+Shift+S (Snipping Tool)
      if (e.metaKey && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        return;
      }
      // Cmd+S / Ctrl+S (Save page)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        return;
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // Keep the screen awake for the duration of the assessment so the device
  // doesn't dim/lock mid-session (especially on mobile). This is a best-effort
  // hint: the OS may still release the lock under battery-saver/low battery, and
  // the lock is auto-released whenever the page is hidden — so we re-acquire it
  // when the page becomes visible again. Keeping the screen on also reduces the
  // screen-lock false positives that the tab-switch warning would otherwise hit.
  useEffect(() => {
    const wakeLockNavigator = navigator as NavigatorWithWakeLock;
    if (!wakeLockNavigator.wakeLock) return; // unsupported (e.g. older iOS Safari)

    let wakeLock: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const requestWakeLock = async () => {
      // Only valid while the page is visible; a request when hidden throws.
      if (document.visibilityState !== 'visible') return;
      try {
        const sentinel = await wakeLockNavigator.wakeLock!.request('screen');
        if (cancelled) {
          // Effect was cleaned up while awaiting — don't hold a stray lock.
          void sentinel.release().catch(() => {});
          return;
        }
        wakeLock = sentinel;
        // If the OS releases it (battery saver, etc.), drop our stale reference
        // so the next visibility change re-acquires cleanly.
        wakeLock.addEventListener('release', () => {
          wakeLock = null;
        });
      } catch {
        // Denied / unsupported / blocked — silently degrade to default timeout.
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && wakeLock === null) {
        void requestWakeLock();
      }
    };

    void requestWakeLock();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock) {
        void wakeLock.release().catch(() => {});
        wakeLock = null;
      }
    };
  }, []);

  // Resolve a field to its effective value: the live form value if present,
  // otherwise the persisted Zustand backup (which survives section unmounts).
  const makeSnapshotResolver = (formValues: Record<string, any>) => (fieldName: string) => {
    const live = formValues[fieldName];
    return valueIsPresent(live) ? live : persistedResponses[fieldName];
  };

  const countUnansweredQuestions = (): number => {
    const resolve = makeSnapshotResolver(getValues());
    return Object.values(categorizedQuestions)
      .flat()
      .filter((q) => isConditionalQuestionVisible(q, gateValues))
      .filter((q) => isQuestionUnanswered(q, resolve))
      .length;
  };

  // Per-section breakdown of unanswered questions, in section order, dropping any
  // section that is fully answered. Drives the modal's "Unanswered Questions"
  // summary so the candidate sees exactly which sections still need attention.
  const getUnansweredSections = (): { id: string; label: string; count: number }[] => {
    const resolve = makeSnapshotResolver(getValues());
    return questionSections
      .map((section) => ({
        id: section.id,
        label: section.label,
        count: section.questions
          .filter((q) => isConditionalQuestionVisible(q, gateValues))
          .filter((q) => isQuestionUnanswered(q, resolve))
          .length,
      }))
      .filter((section) => section.count > 0);
  };

  // Live unanswered check for review-mode badges. Reads through `watch` so the
  // badge clears the moment the candidate answers, then falls back to the
  // persisted backup for fields not currently mounted.
  const isQuestionUnansweredLive = (question: Question): boolean => {
    const resolve = (fieldName: string) => {
      const live = watch(fieldName);
      return valueIsPresent(live) ? live : persistedResponses[fieldName];
    };
    return isQuestionUnanswered(question, resolve);
  };

  const countAnsweredQuestions = (): number => {
    return totalSteps - countUnansweredQuestions();
  };

  const handleSubmitClick = () => {
    if (!isLast) return;
    setIsSubmitModalOpen(true);
  };

  const onSubmit = async () => {
    if (!assessmentState?.assessmentId) {
      // Previously failed silently — the modal's confirm button looked dead and
      // candidates assumed the platform was broken ("I couldn't submit").
      openToast({
        show: true,
        heading: 'Unable to submit',
        text: 'We could not submit your assessment. Please refresh the page and try again.',
        type: 'error',
        timeOut: 5000,
      });
      return;
    }

    // Merge the live form values with persisted backups so no answers from
    // earlier sections are lost during navigation. Same source used by the
    // timeout and tab-switch auto-submit paths.
    const mergedValues = collectMergedFormValues();

    const allQuestions = Object.values(categorizedQuestions)
      .flat()
      .filter((q) => isConditionalQuestionVisible(q, mergedValues));
    // An unanswered question is fine — a part-written one is not.
    const hasTooShortResponse = allQuestions.some((question) => {
      if (question.type !== 'text' && question.type !== 'open_ended') return false;
      const value = mergedValues[`question_${question.id}`];
      return typeof value === 'string' && !meetsOpenEndedResponseMinimum(value);
    });

    if (hasTooShortResponse) {
      openToast({
        show: true,
        heading: 'Response too short',
        text: `Open-ended responses must be at least ${OPEN_ENDED_RESPONSE_MIN_WORDS} words, or left empty. Please review your answers before submitting.`,
        type: 'error',
        timeOut: 5000,
      });
      return;
    }

    const minutesLeft = endTime
      ? Math.max(0, Math.floor((endTime.getTime() - Date.now()) / 60000))
      : (assessmentState?.minutesLeft || 0);
    const sectionedPayloads = buildSectionedPayloads(mergedValues, allQuestions, { minutesLeft });
    const assessment_response_id = localStorage.getItem('assessment_response_id');

    try {
      if (!assessment_response_id) {
        openToast({ show: true, heading: 'Error', text: 'Assessment response ID is missing.', type: 'error', timeOut: 5000 });
        return;
      }

      setIsSubmitModalOpen(false);

      await finalizeAssessmentSubmission(assessment_response_id, sectionedPayloads, 'manual_submit');
    } catch (error) {
      console.error('Failed to submit assessment:', error);
      openToast({
        show: true,
        heading: 'Error',
        text: 'Failed to submit assessment. Please try again.',
        type: 'error',
        timeOut: 5000,
      });
    }
  };

  // Ref to track if initial form reset has been done
  const hasInitializedForm = useRef(false);

  useEffect(() => {
    if (Object.keys(categorizedQuestions).length === 0) return;
    
    // Only reset the form once when categorizedQuestions first becomes available
    if (hasInitializedForm.current) return;
    hasInitializedForm.current = true;

    const allQuestions = Object.values(categorizedQuestions).flat();
    const defaultValues: FormValues = {};
    
    allQuestions.forEach((q) => {
      const fieldName = q.type === 'productivity_tools' ? 'tools' : `question_${q.id}`;
      
      if (q.type === 'productivity_tools') {
        defaultValues[fieldName] = [{ tool: "", proficiency: { value: "", label: "" } }];
      } else if (q.type === 'single_choice_with_rating' || q.type === 'statement_rating') {
        // Initialize both the option and rating fields
        defaultValues[fieldName] = '';
        defaultValues[`${fieldName}Rating`] = '';
      } else if (q.type === 'multiple_choice' || q.type === 'matching') {
        defaultValues[fieldName] = '';
      } else if (q.type === 'multi_select' || q.type === 'multiple_select') {
        defaultValues[fieldName] = [];
      } else if (q.type === 'vertical_ranking') {
        // Initialize with empty array for vertical ranking
        defaultValues[fieldName] = [];
      } else if (q.type === 'ranking') {
        defaultValues[fieldName] = Array(q.options?.length || 6).fill(null);
      } else if (q.type === 'scale' || q.type === 'rating') {
        defaultValues[fieldName] = '';
      } else if (q.type === 'multiple_rating') {
        // Initialize as empty object for multiple rating
        defaultValues[fieldName] = {};
      } else if (q.type === 'text' || q.type === 'open_ended') {
        defaultValues[fieldName] = '';
      } else if (q.type === 'yes_no' || q.type === 'true_false') {
        defaultValues[fieldName] = '';
      } else {
        defaultValues[fieldName] = '';
      }
    });
    
    // Collect all persisted responses from multiple sources
    const valuesToSet = { ...defaultValues };
    
    // Priority 1: Zustand persisted store (most reliable as it uses zustand/persist)
    const zustandResponses = useResponsesStore.getState().responses;
    
    // Priority 2: localStorage backup (assessment_form_state)
    const localStorageState = loadFormStateFromLocalStorage();
    
    // Priority 3: Redux state (legacy)
    const reduxResponses = assessmentState?.responses;
    
    // Merge in order of priority (lower priority first, so higher priority overwrites)
    
    // First, apply Redux responses if available
    if (reduxResponses && Object.keys(reduxResponses).length > 0) {
      Object.keys(reduxResponses).forEach(key => {
        const val = reduxResponses[key];
        if (val !== undefined && val !== null && val !== '') {
          valuesToSet[key] = val;
        }
      });
    }
    
    // Then, apply localStorage backup
    if (localStorageState && Object.keys(localStorageState).length > 0) {
      Object.keys(localStorageState).forEach(key => {
        const val = localStorageState[key];
        if (val !== undefined && val !== null && val !== '') {
          valuesToSet[key] = val;
        }
      });
    }
    
    // Finally, apply Zustand store (highest priority)
    if (zustandResponses && Object.keys(zustandResponses).length > 0) {
      Object.keys(zustandResponses).forEach(key => {
        const val = zustandResponses[key];
        if (val !== undefined && val !== null && val !== '') {
          valuesToSet[key] = val;
        }
      });
    }
    
    reset(valuesToSet);
    
    // Sync all loaded values back to Zustand to ensure consistency
    const nonEmptyValues: Record<string, any> = {};
    Object.keys(valuesToSet).forEach(key => {
      const val = valuesToSet[key];
      if (val !== undefined && val !== null && val !== '' && 
          !(Array.isArray(val) && val.length === 0) && 
          !(typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0)) {
        nonEmptyValues[key] = val;
      }
    });
    if (Object.keys(nonEmptyValues).length > 0) {
      setResps(nonEmptyValues);
    }
  }, [categorizedQuestions, reset, assessmentState?.responses, setResps]);

  // Wrapper to set value and immediately sync that specific field to Redux/localStorage
  const setValueAndSync = (name: string, value: any, options?: any) => {
    setValue(name, value, options);
    const latest = getValues(name);
    setResp(name, latest);
  };

  // Initialize multipleRatingStates from persisted form data
  useEffect(() => {
    if (Object.keys(categorizedQuestions).length === 0) return;

    const allQuestions = Object.values(categorizedQuestions).flat();
    const multipleRatingQuestions = allQuestions.filter(q => q.type === 'multiple_rating');
    
    if (multipleRatingQuestions.length > 0) {
      const newStates: { [key: string]: { [key: string]: number } } = {};
      
      multipleRatingQuestions.forEach(q => {
        const fieldName = `question_${q.id}`;
        const persistedValue = getValues(fieldName) || {};
        const activities = q.activities || q.statements || [];
        
        const state: { [key: string]: number } = {};
        activities.forEach((_, index) => {
          const key = String.fromCharCode(97 + index); // a, b, c, d, e
          state[key] = persistedValue[key] || 0;
        });
        
        newStates[fieldName] = state;
      });
      
      setMultipleRatingStates(newStates);
    }
  }, [categorizedQuestions, getValues]);

  // Subscribe to all field changes and sync only the changed field to Zustand
  useEffect(() => {
    const subscription = watch((formData, info) => {
      const changedName = (info as any)?.name;
      if (changedName) {
        const latest = formData[changedName];
        setResp(changedName, latest);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setResp]);

  // Auto-save to localStorage periodically (every 30 seconds)
  useEffect(() => {
    if (Object.keys(categorizedQuestions).length === 0) return;
    
    const autoSaveInterval = setInterval(() => {
      const currentFormValues = getValues();
      if (currentFormValues && Object.keys(currentFormValues).length > 0) {
        const allQuestions = Object.values(categorizedQuestions).flat();
        saveFormStateToLocalStorage(currentFormValues, allQuestions.map(q => q.id));
        setResps(currentFormValues);
      }
    }, 30000); // 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [categorizedQuestions, getValues, setResps]);


  const totalSteps = questionSections.reduce(
    (sum, section) => sum + section.questions.filter((q) => isConditionalQuestionVisible(q, gateValues)).length,
    0,
  );

  const sidebarItems = questionSections.map((section) => ({
    id: section.id,
    label: section.label,
    count: `${section.questions.filter((q) => isConditionalQuestionVisible(q, gateValues)).length}`,
  }));

  const getCurrentSectionQuestions = (): Question[] => {
    return (categorizedQuestions[activeSection] || []).filter((q) => isConditionalQuestionVisible(q, gateValues));
  };

  const getCurrentPageQuestions = (): Question[] => {
    const sectionQuestions = getCurrentSectionQuestions();
    const currentPage = currentPages[activeSection] || 1;
    const startIndex = (currentPage - 1) * questionsPerPage;
    const endIndex = startIndex + questionsPerPage;
    return sectionQuestions.slice(startIndex, endIndex);
  };

  const getTotalPagesForCurrentSection = (): number => {
    const sectionQuestions = getCurrentSectionQuestions();
    return Math.ceil(sectionQuestions.length / questionsPerPage);
  };

  const sidebarRef = useRef<HTMLDivElement>(null);
  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const { isMobile } = useMobile({ size: 1024 });

  useClickOutside(sidebarRef, () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [isMobile]);

  const handleSectionClick = (sectionId: string) => {
    const currentFormValues = getValues();
    if (currentFormValues && Object.keys(currentFormValues).length > 0) {
      setResps(currentFormValues);
      persistSectionToServer(activeSection, currentFormValues);
    }

    setActiveSection(sectionId);
    setCurrentPages(prev => ({
      ...prev,
      [sectionId]: 1,
    }));
  };

  // Reset the questions section (the page of questions) to its top after
  // navigating, so a new page never opens part-way down. Runs after the new
  // page has rendered.
  const scrollQuestionsToTop = () => {
    requestAnimationFrame(() => {
      questionsContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const handleNext = () => {
    const totalPages = getTotalPagesForCurrentSection();
    const currentPage = currentPages[activeSection];

    // Persist locally and post the active section to the server progressively
    // so partial progress survives refresh/disconnect. The final Submit acts
    // as a safety net by re-posting all sections.
    const currentFormValues = getValues();
    if (currentFormValues && Object.keys(currentFormValues).length > 0) {
      setResps(currentFormValues);
      const allQuestions = questionSections.flatMap((section) => section.questions);
      saveFormStateToLocalStorage(currentFormValues, allQuestions.map(q => q.id));
      persistSectionToServer(activeSection, currentFormValues);
    }

    // Navigate to next page/section
    if (currentPage < totalPages) {
      setCurrentPages(prev => ({
        ...prev,
        [activeSection]: currentPage + 1,
      }));
    } else {
      const currentIndex = sidebarItems.findIndex((item) => item.id === activeSection);
      const nextItem = sidebarItems[currentIndex + 1];
      if (nextItem) {
        setActiveSection(nextItem.id);
        setCurrentPages(prev => ({
          ...prev,
          [nextItem.id]: 1,
        }));
      }
    }

    // Scroll questions container to top after navigating
    scrollQuestionsToTop();
  };

  const handlePrevious = () => {
    const currentPage = currentPages[activeSection];

    const currentFormValues = getValues();
    if (currentFormValues && Object.keys(currentFormValues).length > 0) {
      setResps(currentFormValues);
      persistSectionToServer(activeSection, currentFormValues);
    }

    if (currentPage > 1) {
      setCurrentPages(prev => ({
        ...prev,
        [activeSection]: currentPage - 1,
      }));
    } else {
      const currentIndex = sidebarItems.findIndex((item) => item.id === activeSection);
      const prevItem = sidebarItems[currentIndex - 1];
      if (prevItem) {
        setActiveSection(prevItem.id);
        // Count only conditionally-visible questions so the landing page matches
        // the paging used everywhere else. Using the raw list here can overshoot
        // the last real page and render an empty "No questions" state.
        const prevSectionQuestions = (categorizedQuestions[prevItem.id] || []).filter((q) =>
          isConditionalQuestionVisible(q, gateValues)
        );
        const totalPagesInPrevSection = Math.max(
          1,
          Math.ceil(prevSectionQuestions.length / questionsPerPage)
        );
        setCurrentPages(prev => ({
          ...prev,
          [prevItem.id]: totalPagesInPrevSection,
        }));
      }
    }

    // Always land at the top of the previous page (not the bottom).
    scrollQuestionsToTop();
  };

  // Jump to the first section/page that still has an unanswered question so the
  // candidate lands right where they need to act (used by "Review Questions").
  const goToFirstUnanswered = () => {
    const resolve = makeSnapshotResolver(getValues());
    for (const section of questionSections) {
      const visibleQuestions = section.questions.filter((q) => isConditionalQuestionVisible(q, gateValues));
      const unansweredIndex = visibleQuestions.findIndex((q) => isQuestionUnanswered(q, resolve));
      if (unansweredIndex !== -1) {
        const page = Math.floor(unansweredIndex / questionsPerPage) + 1;
        setActiveSection(section.id);
        setCurrentPages((prev) => ({ ...prev, [section.id]: page }));
        scrollQuestionsToTop();
        return;
      }
    }
  };

  const handleReviewQuestions = () => {
    setIsSubmitModalOpen(false);
    setReviewMode(true);
    goToFirstUnanswered();
  };

  const colors = [
    '#D0CFFF', // Ideation
    '#F6CDCD', // Prototyping
    '#BAB9FF', // Testing
    '#DCFFBA', // Implementation
    '#F1D2FF', // Market launch
    '#CDF6FF', // Post-launch review
  ];

  const renderQuestion = (question: Question, index: number) => {
    let questionNumber = index + 1;
    const currentPage = currentPages[activeSection] || 1;
    questionNumber = ((currentPage - 1) * questionsPerPage) + index + 1;
    const name = `question_${question.id}`;

    if (question.type === 'multiple_choice' || question.type === 'matching') {
      const selectedValue = watch(name);
      const hasOthersSelected = selectedValue && 
        (selectedValue.toLowerCase().includes('others') || 
         selectedValue.toLowerCase().includes('other'));
      
      return (
        <div key={name}>
          <QuestionSingleRadio
            register={register}
            watch={watch}
            errors={errors}
            name={name}
            options={question.options || []}
            questionText={question.question || question.text || ''}
            questionNumber={questionNumber}
            containerClassName={styles.radioContainer}
            titleClassName={styles.radioTitle}
          />
          
          {/* Show text input when "Others" is selected */}
          {question.hasOthersOption && hasOthersSelected && question.othersField && (
            <div className={styles.othersInputContainer}>
              <input
                type="text"
                placeholder="Please specify..."
                {...register(question.othersField)}
                className={styles.othersInput}
              />
              {errors[question.othersField] && (
                <span className={styles.errorMessage}>
                  {errors[question.othersField]?.message as string}
                </span>
              )}
            </div>
          )}
        </div>
      );
    } else if (question.type === 'single_choice_with_rating') {
      return (
        <QuestionCheckboxWithRating
          key={name}
          register={register}
          errors={errors}
          name={name}
          watch={watch}
          setValue={setValueAndSync}
          ratingName={`${name}Rating`}
          options={question.options || []}
          questionText={question.question || question.text || ''}
          questionNumber={questionNumber}
          labelText="Select the most likely option and rate how operationally relevant it is to you."
          containerClassName={styles.radioContainer}
          titleClassName={styles.radioTitle}
          radioContainerClassName={`${styles.radioOptionsContainer} ${styles.singleRadioOptionsContainer}`}
        />
      );
    } else if (question.type === 'multi_select' || question.type === 'multiple_select') {
      return (
        <QuestionMultiCheckbox
          key={name}
          register={register}
          errors={errors}
          name={name}
          options={question.options || []}
          questionText={question.question || question.text || ''}
          questionNumber={questionNumber}
          containerClassName={styles.radioContainer}
          titleClassName={styles.radioTitle}
          radioContainerClassName={`${styles.radioOptionsContainer} ${styles.multiSelectOptionsContainer}`}
          watch={watch}
          maxSelections={question.select_count || 3}
          labelText={`Select the crucial ${question.select_count || 3}`}
        />
      );
    } else if (question.type === 'scale' || question.type === 'rating') {
      return (
        <QuestionRanking
          register={register}
          watch={watch}
          setValue={setValueAndSync}
          errors={errors}
          name={name}
          options={Array.from({ length: ((question.scale_max || 5) - (question.scale_min || 1) + 1) }, (_, i) => ({
            label: '',
            value: (i + (question.scale_min || 1)).toString(),
          }))}
          questionText={question.text || question.question || ''}
          questionNumber={questionNumber}
          statement={''}
          labelText="Rank on a scale from most likely fit to the least likely fit"
          minLabel={question.scale_min_label}
          maxLabel={question.scale_max_label}
        />
      );
    } else if (question.type === 'statement_rating') {
      // For statement_rating from personality section, options are the statements
      const statements = question.statements || question.options?.map(opt => opt.label || opt.text || '') || [];
      return (
        <QuestionCheckboxWithRating
          key={name}
          register={register}
          errors={""}
          name={name}
          watch={watch}
          setValue={setValueAndSync}
          ratingName={`${name}Rating`}
          options={statements.map((stmt, idx) => ({
            label: stmt,
            value: stmt,
            id: `${question.id}_stmt_${idx}`,
            text: stmt
          }))}
          questionText={question.text || question.question || ''}
          questionNumber={questionNumber}
          labelText="Select the most likely option and rate how operationally relevant it is to you."
          containerClassName={styles.radioContainer}
          titleClassName={styles.radioTitle}
          subtitleClassName='pl-4'
          radioContainerClassName={`${styles.radioOptionsContainer} ${styles.singleRadioOptionsContainer}`}
        />
      );
    } else if (question.type === 'vertical_ranking') {
      return (
        <VerticalRankingField
          key={name}
          control={control}
          register={register}
          watch={watch}
          setValue={setValueAndSync}
          trigger={trigger}
          errors={errors}
          name={name}
          questionText={question.text || question.question || 'Arrange the items in order of significance'}
          subtitle="Rank these from 1 (your most likely action) to 4 (your least likely action)"
          questionNumber={questionNumber}
          items={question.options?.filter(opt => opt != null).map(opt => ({
            id: opt.id || opt.value,
            text: opt.label,
            value: opt.value,
            rank: opt.rank
          })) || []}
          containerClassName={styles.radioContainer}
          titleClassName={styles.radioTitle}
          subtitleClassName={styles.radioSubtitle}
        />
      );
    } else if (question.type === 'ranking') {
      return (
        <RankingField
          key={name}
          control={control}
          register={register}
          watch={watch}
          setValue={setValueAndSync}
          trigger={trigger}
          errors={errors}
          name={name}
          questionText={question.text || 'Rank these items in order of priority'}
          questionNumber={questionNumber}
          items={question.items?.filter((text: string) => text != null).map((text: string, index: number) => ({
            id: `${index}-${text}`,
            text,
            color: colors[index] || '#f0f0f0',
            position: index + 1
          })) || []}
        />
      )
    } else if (question.type === 'text') {
      return (
        <QuestionTextarea
          key={name}
          register={register}
          watch={watch}
          errors={errors}
          name={name}
          questionText={question.question || question.text || ''}
          questionNumber={questionNumber}
          minWords={OPEN_ENDED_RESPONSE_MIN_WORDS}
          placeholder="Enter your response here"
          containerClassName={styles.textareaContainer}
          titleClassName={styles.radioTitle}
          textareaContainerClassName={styles.textareaInnerContainer}
          textareaClassName={styles.answerTextarea}
          characterCountClassName={styles.characterCount}
          errorMessageClassName={styles.message}
        />
      );
    } else if (question.type === 'productivity_tools') {
      return (
        <ProductivityToolsInput
          key="tools"
          control={control}
          register={register}
          watch={watch}
          setValue={setValueAndSync}
          trigger={trigger}
          errors={errors}
          name="tools"
          questionText={question.question || question.text || 'Input five (5) work and productivity tools you are most familiar with and rate on a scale of how proficiently you use them.'}
          questionNumber={questionNumber}
          containerClassName={styles.radioContainer}
          titleClassName={styles.radioTitle}
          subtitleClassName={styles.radioSubtitle}
        />
      );
    } else if (question.type === 'yes_no' || question.type === 'true_false') {
      // Gate questions like leadership experience or persuasion gate
      const options = question.type === 'yes_no' 
        ? [{ label: 'Yes', value: 'true', text: 'Yes' }, { label: 'No', value: 'false', text: 'No' }]
        : [{ label: 'True', value: 'true', text: 'True' }, { label: 'False', value: 'false', text: 'False' }];
      
      return (
        <QuestionSingleRadio
          key={name}
          register={register}
          errors={errors}
          name={name}
          options={options}
          questionText={question.question || question.text || ''}
          questionNumber={questionNumber}
          containerClassName={styles.radioContainer}
          titleClassName={styles.radioTitle}
        />
      );
    } else if (question.type === 'multiple_rating') {
      // For admin, empathy activities - rate multiple items on a scale
      const activities = question.activities || question.statements || [];
      const skills = activities.map((activity, index) => ({
        key: String.fromCharCode(97 + index), // a, b, c, d, e
        label: activity,
      }));

      // Fall back to an empty object if the init effect hasn't populated state yet.
      // Calling setState here would cascade and trip React's max-update-depth guard.
      const currentRatings = multipleRatingStates[name] || {};

      const handleRatingChange = (skill: string, rating: number) => {
        setMultipleRatingStates(prev => ({
          ...prev,
          [name]: {
            ...(prev[name] || {}),
            [skill]: rating,
          },
        }));
        
        // Update form value
        const currentValue = getValues(name) || {};
        setValueAndSync(name, { ...currentValue, [skill]: rating }, { shouldDirty: true });
      };

      return (
        <QuestionSkillsRating
          key={name}
          selectedSkills={currentRatings}
          handleRatingChange={handleRatingChange}
          questionText={question.question || question.text || ''}
          questionNumber={questionNumber}
          skills={skills}
          scaleMax={question.scale_max || 7}
          errorMessage={errors[name]?.message as string | undefined}
          containerClassName={styles.radioContainer}
          titleClassName={styles.radioTitle}
        />
      );
    } else if (question.type === 'open_ended') {
      // For open-ended questions from domain knowledge
      return (
        <QuestionTextarea
          key={name}
          register={register}
          watch={watch}
          errors={errors}
          name={name}
          questionText={question.question || question.text || ''}
          questionNumber={questionNumber}
          minWords={OPEN_ENDED_RESPONSE_MIN_WORDS}
          placeholder="Enter your response here"
          containerClassName={styles.textareaContainer}
          titleClassName={styles.radioTitle}
          textareaContainerClassName={styles.textareaInnerContainer}
          textareaClassName={styles.answerTextarea}
          characterCountClassName={styles.characterCount}
          errorMessageClassName={styles.message}
        />
      );
    }
    return null;
  };

  const getCurrentQuestions = () => {
    if (hasError) return null;

    const questions = getCurrentPageQuestions();

    if (questions.length === 0 && !isLoadingQuestions) {
      return <div>No questions available for this section.</div>;
    }

    return questions.map((question, index) => {
      const showUnanswered = reviewMode && isQuestionUnansweredLive(question);
      return (
        <div
          key={question.id}
          className={showUnanswered ? 'relative border-l-[3px] border-[#F59E0B] bg-[#FFF9F0] rounded-r-lg pl-4 pr-28 py-3 mb-5' : undefined}
        >
          {showUnanswered && (
            <span className="absolute top-3 right-4 z-10 rounded-full border border-[#F59E0B] bg-white px-3 py-1 text-[11px] font-medium text-[#D97706]">
              Unanswered
            </span>
          )}
          {renderQuestion(question, index)}
        </div>
      );
    });
  };

  const activeLabel = sidebarItems.find((item) => item.id === activeSection)?.label;

  const currentIndex = sidebarItems.findIndex((item) => item.id === activeSection);
  const isFirst = currentIndex === 0 && currentPages[activeSection] === 1;
  const currentPage = currentPages[activeSection] || 1;
  const totalPages = getTotalPagesForCurrentSection();
  const isLast =
    sidebarItems.length > 0 &&
    currentIndex === sidebarItems.length - 1 &&
    totalPages > 0 &&
    currentPage >= totalPages;

  useEffect(() => {
    if (id) {
      fetchAssessmentInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  // Load assessment response data from localStorage (saved from profile form submission)
  useEffect(() => {
    if (id) {
      // Try to get assessment response data from localStorage first
      const savedResponseData = localStorage.getItem('assessment_response_data');
      
      if (savedResponseData) {
        try {
          const responseData = JSON.parse(savedResponseData);
          
          // Set the assessment response ID
          if (responseData.id) {
            dispatch(setAssessmentId(responseData.id.toString()));
            localStorage.setItem('assessment_response_id', responseData.id.toString());
          }

          // Use end_time from saved data as source of truth
          if (responseData.end_time) {
            const endTimeDate = new Date(responseData.end_time);
            setEndTime(endTimeDate);
            
            // Calculate minutes left based on end_time
            const minutesLeft = Math.max(0, Math.floor((endTimeDate.getTime() - Date.now()) / 60000));
            dispatch(updateMinutesLeft(minutesLeft));
          } else if (responseData.duration_minutes_left !== undefined) {
            // Fallback to duration_minutes_left if end_time not available
            const endTimeDate = new Date(Date.now() + responseData.duration_minutes_left * 60000);
            setEndTime(endTimeDate);
            dispatch(updateMinutesLeft(responseData.duration_minutes_left));
          }
        } catch (error) {
          console.error('Failed to parse saved assessment response data:', error);
        }
      } else {
        // Fallback to check endpoint if no saved data (backward compatibility)
        const candidateEmail = localStorage.getItem('candidate_email');
        
        if (candidateEmail) {
          checkCandidateProgress(
            { assessment: id, email: candidateEmail },
            {
              onSuccess: (data) => {
                if (data.data.response_data?.id) {
                  dispatch(setAssessmentId(data.data.response_data.id.toString()));
                  localStorage.setItem('assessment_response_id', data.data.response_data.id.toString());

                  const responseData = data.data.response_data as any;
                  if (responseData.end_time) {
                    const endTimeDate = new Date(responseData.end_time);
                    setEndTime(endTimeDate);
                    
                    const minutesLeft = Math.max(0, Math.floor((endTimeDate.getTime() - Date.now()) / 60000));
                    dispatch(updateMinutesLeft(minutesLeft));
                  } else if (data.data.response_data.duration_minutes_left !== undefined) {
                    const endTimeDate = new Date(Date.now() + data.data.response_data.duration_minutes_left * 60000);
                    setEndTime(endTimeDate);
                    dispatch(updateMinutesLeft(data.data.response_data.duration_minutes_left));
                  }
                }
              },
              onError: (error) => {
                console.error('Failed to check candidate progress:', error);
              },
            }
          );
        }
      }
    }
  }, [id, checkCandidateProgress, dispatch]);

  // Set assessment response ID when assessment info is loaded (fallback)
  useEffect(() => {
    if (assessmentInfo?.data?.id) {
      dispatch(setAssessmentId(assessmentInfo.data.id.toString()));
    }
  }, [assessmentInfo, dispatch]);

  // Fallback: use duration_in_minutes from assessment info when no other
  // timing source has set endTime (e.g. first load, no localStorage data).
  // Persists the computed end_time so reloads don't restart the clock.
  useEffect(() => {
    if (!endTime && assessmentInfo?.data?.duration_in_minutes) {
      const endTimeDate = new Date(Date.now() + assessmentInfo.data.duration_in_minutes * 60000);
      setEndTime(endTimeDate);
      dispatch(updateMinutesLeft(assessmentInfo.data.duration_in_minutes));

      // Persist so the lazy initializer picks it up on reload
      try {
        const saved = localStorage.getItem('assessment_response_data');
        const data = saved ? JSON.parse(saved) : {};
        data.end_time = endTimeDate.toISOString();
        localStorage.setItem('assessment_response_data', JSON.stringify(data));
      } catch {}
    }
  }, [endTime, assessmentInfo, dispatch]);


  const isExpired = useMemo(() => {
    if (!assessmentInfo?.data?.end_date) return false;
    return new Date() > new Date(assessmentInfo.data.end_date);
  }, [assessmentInfo?.data?.end_date]);

  // If the candidate already submitted or their timer has elapsed, render
  // nothing while the redirect effect above navigates away. This prevents
  // the questions UI, form subscriptions, and CountDown from mounting and
  // churning state after the assessment is effectively over.
  if (shouldBlockAssessment) {
    return null;
  }

  // Show a minimal loading screen while assessment info is loading so the
  // sidebar / timer never flash before we know whether the link is expired.
  if (isLoadingGetAssessmentInfo) {
    return (
      <div className={`${styles.container} ${styles.assessmentContainer}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Spinner className={`${styles.spinner} ${styles.logoSpinner}`} />
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="relative min-h-screen overflow-hidden">
        <nav className="bg-[#D1E9FF] px-[5%] py-3 border-b border-[#eee] h-[min(11vw,130px)] flex items-center gap-6 fixed top-0 w-full z-[999] max-sm:h-auto">
          <div className="flex items-center gap-6">
            {assessmentInfo?.data?.company_info?.company_logo && (
              <img
                className="h-[45px] w-auto"
                src={assessmentInfo?.data?.company_info?.company_logo}
                alt="employer logo"
              />
            )}
            <h3 className="text-2xl font-semibold text-[#012169]">
              {assessmentInfo?.data?.company_info?.name}
            </h3>
          </div>
        </nav>
        <div className="flex items-center justify-center mt-[min(11vw,130px)] h-[calc(100vh-130px)] max-sm:mt-[70px] max-sm:h-[calc(100vh-70px)]">
          <div className="text-center p-8">
            <h3 className="text-2xl font-semibold text-[#012169] mb-3">
              Assessment Link No Longer Accessible
            </h3>
            <p className="text-sm text-[#475467] max-w-[420px] mx-auto">
              This assessment has expired and is no longer accepting responses. Please contact the employer for further assistance.
            </p>
          </div>
        </div>
        <footer className="fixed bottom-10 left-[50px] flex flex-col items-start justify-center w-fit gap-[7px] text-[#6b6b75] text-[13px] max-sm:bottom-5 max-sm:left-[30px] max-sm:text-[11px] max-sm:gap-1">
          <span className="text-[10px] max-sm:text-[8px]">Enabled by</span>
          <LogoWithText className="w-[90px] max-sm:w-[70px]" />
        </footer>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={`${styles.container} ${styles.assessmentContainer}`}>
        <nav className={`${styles.nav} ${styles.assessmentNav}`}>
          <div className={`${styles.logoSection} ${styles.logoContainer}`}>
            <h3 className={`${styles.title} ${styles.errorTitle}`}>
              Error Loading Assessment
            </h3>
          </div>
          {countdownDate && <CountDown date={countdownDate} handleTimeout={handleTimeout} />}
        </nav>
        <div className={`${styles.content} ${styles.contentContainer}`}>
          <div className={styles.errorContainer}>
            <h3 className={styles.errorTitle}>Unable to Load Assessment</h3>
            <p className={styles.errorMessage}>
              There was an error fetching the assessment data. Please try again later or contact support.
            </p>
          </div>
        </div>
        <footer className={`${styles.footerLogo} ${styles.footerContainer}`}>
          <span className={styles.footerText}>Enabled by</span>
          <LogoWithText className={styles.footerLogoIcon} />
        </footer>
      </div>
    );
  }

  return (
    <div
      className={`${styles.container} ${styles.assessmentContainer}`}
      style={{ userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' } as React.CSSProperties}
    >
      <nav className={`${styles.nav} ${styles.assessmentNav}`}>
        <div className={`${styles.logoSection} ${styles.logoContainer}`}>
          {assessmentInfo?.data?.company_info?.company_logo && (
            <img
              className={`${styles.logo} ${styles.companyLogo}`}
              src={assessmentInfo?.data?.company_info?.company_logo}
              alt="employer logo"
            />
          )}
          <h3 className={`${styles.title} ${styles.companyTitle}`}>
            {assessmentInfo?.data?.company_info?.name || '--/--'}
          </h3>
        </div>
        {countdownDate && <CountDown date={countdownDate} handleTimeout={handleTimeout} />}
      </nav>

      <div className={`${styles.layout} ${styles.mainLayout}`}>
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              className={`${styles.sidebar} ${styles.sidebarContainer}`}
              initial={{ x: '-100%' }}
              animate={{ x: 0, transition: { type: 'tween', duration: 0.3 } }}
              exit={{ x: '-100%', transition: { type: 'tween', duration: 0.3 } }}
              ref={sidebarRef}
            >
              <div className={`${styles.sidebarHeader} ${styles.sidebarHeaderContainer}`}>
                <h3 className={`${styles.sidebarTitle} ${styles.assessmentTitle}`}>
                  Assessment
                </h3>
                <ArrowLeft
                  className={`${styles.arrow} ${styles.sidebarArrow}`}
                  onClick={toggleSidebar}
                />
              </div>
              <div className={`${styles.sidebarNavigation} ${styles.navigationContainer}`}>
                {sidebarItems.map((item) => (
                  <div
                    key={item.id}
                    className={`${styles.sidebarItem} ${activeSection === item.id ? styles.active : ''} ${styles.navItem}`}
                    onClick={() => handleSectionClick(item.id)}
                  >
                    <span className={`${styles.sidebarItemLabel} ${styles.itemLabel}`}>
                      {item.label}
                    </span>
                    <span className={`${styles.sidebarItemCount} ${styles.itemCount}`}>
                      ({item.count})
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
          {!isSidebarOpen && (
            <MenuIcon
              className={`${styles.hamburger} ${styles.absoluteHamburger}`}
              onClick={toggleSidebar}
            />
          )}
        </AnimatePresence>

        <div className={`${styles.content} ${styles.contentContainer}`}>
          <div className={`${styles.header} ${styles.contentHeader}`}>
            <ProgressSidebar
              currentStep={countAnsweredQuestions()}
              totalSteps={totalSteps}
              title="Your Progress"
              className={styles.progressContainer}
            />
            <div className={`${styles.sectionHeader} ${styles.sectionHeaderContainer}`}>
              <h3 className={`${styles.title} ${styles.sectionTitle}`}>
                {activeLabel}
              </h3>
            </div>
          </div>
          <form onSubmit={(e) => e.preventDefault()}>
            <div ref={questionsContainerRef} className={`${styles.questionsContainer} ${styles.questionsWrapper}`}>
              {isLoadingQuestions ? (
                <Spinner className={`${styles.spinner} ${styles.questionsSpinner}`} />
              ) : (
                <div>
                  {getCurrentQuestions()}
                </div>
              )}
              {totalSteps === 0 && !isLoadingQuestions && (
                <div>No Questions now, check back later</div>
              )}
              <div ref={questionsEndRef} />
            </div>

            {!isLoadingQuestions && totalSteps > 0 && (
              <div className="sticky bottom-0 z-10 flex justify-end gap-2.5 py-2.5 bg-[#F5FAFF] max-sm:px-[5%]">
                <button
                  className="bg-transparent text-black border border-[#012169] ml-0 px-6 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors duration-200 hover:bg-[#f0f0ff] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handlePrevious}
                  disabled={isFirst || isUpdating}
                  type="button"
                >
                  Previous
                </button>
                {isLast ? (
                  <button
                    className="ml-0 px-6 py-2 rounded-md text-sm font-medium cursor-pointer bg-[#012169] text-white border border-[#012169] transition-colors duration-200 hover:bg-[#332fa0] disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                    onClick={handleSubmitClick}
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Submitting...' : 'Submit'}
                  </button>
                ) : (
                  <button
                    className="ml-0 px-6 py-2 rounded-md text-sm font-medium cursor-pointer bg-[#012169] text-white border border-[#012169] transition-colors duration-200 hover:bg-[#332fa0] disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleNext}
                    disabled={isUpdating}
                    type="button"
                  >
                    {isUpdating ? 'Saving...' : 'Next'}
                  </button>
                )}
              </div>
            )}
          </form>
        </div>
      </div>

      <SubmitAssessmentModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        onSubmit={onSubmit}
        onReview={handleReviewQuestions}
        isLoading={isUpdating}
        unansweredQuestionsCount={countUnansweredQuestions()}
        unansweredSections={getUnansweredSections()}
      />

      {/* Tab-switch warning modal */}
      {showTabSwitchWarning && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '40px 32px',
              maxWidth: '440px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#FEF3F2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3
              style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#101828',
                marginBottom: '8px',
              }}
            >
              {warningReason === 'fullscreen-exit'
                ? 'Warning: Fullscreen Exited'
                : 'Warning: Tab Switch Detected'}
            </h3>
            <p
              style={{
                fontSize: '14px',
                color: '#475467',
                lineHeight: '1.6',
                marginBottom: '28px',
              }}
            >
              {warningReason === 'fullscreen-exit' ? (
                <>You have exited fullscreen mode. This assessment must be taken in fullscreen. If you exit fullscreen again, your assessment will be <b>automatically submitted</b> with your current answers.</>
              ) : (
                <>You have navigated away from the assessment. If you switch tabs or leave this page again, your assessment will be <b>automatically submitted</b> with your current answers.</>
              )}
            </p>
            <button
              onClick={() => {
                setShowTabSwitchWarning(false);
                // The click is a fresh user gesture — use it to restore the
                // proctored fullscreen state. No-ops on touch/Safari.
                if (!isTouchDevice) openFullScreen();
              }}
              style={{
                backgroundColor: '#012169',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 32px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              I Understand, Continue Assessment
            </button>
          </div>
        </div>
      )}

      <footer className={`${styles.footerLogo} ${styles.footerContainer}`}>
        <span className={styles.footerText}>Enabled by</span>
        <LogoWithText className={styles.footerLogoIcon} />
      </footer>

    </div>
  );
};

export { AssessmentUI };
